import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useCategories } from "@/hooks/useCategories";
import { ProductCard } from "@/components/ProductCard";
import { useSettings } from "@/context/SettingsContext";
import { HeroBanner } from "@/components/HeroBanner";

const MONDO = "https://static.prod-images.emergentagent.com/jobs/09829b92-385e-4575-aba1-9841550c8b51/images/17981c91888ea69d687aa0d73fdbf489b7a7c9d63b93cac32ac8dd0efebd4c0e.jpeg";

const SectionHead = ({ eyebrow, title, link, testId }) => (
  <div className="flex items-end justify-between gap-6 mb-10">
    <div>
      <div className="text-[11px] uppercase tracking-[0.25em] text-[#956842] mb-3">{eyebrow}</div>
      <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#39251D] leading-none">{title}</h2>
    </div>
    {link && (
      <Link to={link} data-testid={testId} className="group shrink-0 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#39251D] border-b border-[#956842] pb-1">
        Vedi tutto <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
      </Link>
    )}
  </div>
);

const Hero = () => <HeroBanner s={useSettings()} />;

const Categories = () => {
  const cats = useCategories();
  return (
    <section id="collezioni" className="max-w-[1440px] mx-auto px-5 sm:px-8 pt-24 scroll-mt-20" data-testid="home-categories">
      <SectionHead eyebrow="Le categorie" title="Esplora la boutique" />
      <div className="flex lg:grid lg:grid-cols-5 gap-4 sm:gap-5 overflow-x-auto no-scrollbar snap-x -mx-5 px-5 lg:mx-0 lg:px-0">
        {cats.map((c, i) => (
          <Link key={c.slug} to={`/categoria/${c.slug}`} data-testid={`home-category-${c.slug}`} className={`group relative shrink-0 w-[62vw] sm:w-[40vw] lg:w-auto snap-start overflow-hidden aspect-[3/4] ${i < 2 ? "lg:col-span-1" : ""}`}>
            <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#242321]/75 via-transparent to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-5">
              <div className="font-serif text-2xl text-[#F5F0E8]">{c.name}</div>
              <div data-testid={`home-category-count-${c.slug}`} className="text-[10px] uppercase tracking-[0.22em] text-[#D5B98C] mt-1">{c.product_count ? `${c.product_count} ${c.product_count === 1 ? "prodotto" : "prodotti"}` : "In arrivo"}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

const ProductRow = ({ eyebrow, title, params, link, testId }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/products", { params }).then((r) => setItems(r.data)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!items.length) return null;
  return (
    <section className="max-w-[1440px] mx-auto px-5 sm:px-8 pt-24" data-testid={testId}>
      <SectionHead eyebrow={eyebrow} title={title} link={link} testId={`${testId}-view-all`} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 sm:gap-x-6">
        {items.map((p) => <ProductCard key={p.slug} p={p} />)}
      </div>
    </section>
  );
};

const MondoBDC = () => (
  <section className="max-w-[1440px] mx-auto px-5 sm:px-8 pt-28" data-testid="home-mondo-bdc">
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
      <div className="lg:col-span-7 relative">
        <img src={MONDO} alt="Il mondo BDC" className="w-full aspect-[4/3] object-cover" />
        <div className="hidden lg:block absolute -bottom-6 -right-6 w-40 h-40 stitch" />
      </div>
      <div className="lg:col-span-5">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#956842] mb-4">Il mondo BDC</div>
        <h2 className="font-serif text-4xl sm:text-5xl text-[#39251D] leading-[1.02]">Dalla bottega,<br /><em className="text-[#956842]">una nuova boutique.</em></h2>
        <p className="mt-6 text-[15px] leading-relaxed text-[#242321]/80 max-w-md">
          Le nostre radici affondano nell'esperienza de La Bottega del Calzolaio, una realtà familiare nata a Foggia. Da questa esperienza prende vita BDC-Boutique, uno spazio dedicato alla selezione di borse, portafogli, cinture e accessori di pelletteria.
        </p>
        <p className="mt-4 font-serif italic text-xl text-[#956842]">Una nuova identità, le stesse radici, la stessa passione.</p>
        <Link to="/chi-siamo" data-testid="mondo-bdc-cta" className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border-b border-[#956842] pb-1 text-[#39251D]">
          Chi siamo <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  </section>
);

const SECTIONS = {
  hero: () => <Hero />,
  categories: () => <Categories />,
  new_arrivals: () => <ProductRow eyebrow="Appena arrivati" title="Nuovi arrivi" params={{ is_new: true, sort: "novita", limit: 4 }} link="/collezione?sort=novita" testId="home-new-arrivals" />,
  selection: () => <ProductRow eyebrow="Scelti per voi" title="La nostra selezione" params={{ featured: true, limit: 8 }} link="/collezione?sort=evidenza" testId="home-selection" />,
  mondo: () => <MondoBDC />,
};
const DEFAULT_ORDER = Object.keys(SECTIONS).map((id) => ({ id, visible: true }));

export default function Home() {
  const { home_sections } = useSettings();
  return (
    <div data-testid="home-page">
      {(home_sections || DEFAULT_ORDER).filter((s) => s.visible && SECTIONS[s.id]).map((s) => <div key={s.id}>{SECTIONS[s.id]()}</div>)}
    </div>
  );
}
